import React, { useEffect, useState } from "react";
import { Flex, Spin } from "antd";
import DOMPurify from "dompurify";
import { Editor, EditorProvider } from "react-simple-wysiwyg";
import { CommunicationHttpService } from "../../services/Http/CommunicationHttpService";

interface Props {
    body: string;
    communicationId: number;
}

/**
 * Renders a communication body (HTML).
 * Embedded images are stored as <img data-comm-id="X" /> (no src).
 * This component fetches each image as an authenticated blob URL and fills src.
 */
export function CommunicationBodyRenderer({ body, communicationId }: Props) {
    const [html, setHtml] = useState<string | null>(null);

    useEffect(() => {
        if (!body) {
            setHtml("");
            return;
        }

        // Sanitize the body first before touching the DOM.
        const sanitized = DOMPurify.sanitize(body);
        const doc = new DOMParser().parseFromString(sanitized, "text/html");
        const imgs = Array.from(doc.querySelectorAll("img[data-comm-id]"));

        if (imgs.length === 0) {
            setHtml(doc.body.innerHTML);
            return;
        }

        let cancelled = false;
        const blobUrls: string[] = [];

        const ids = imgs.map((img) => Number(img.getAttribute("data-comm-id")));

        Promise.all(ids.map((id) => CommunicationHttpService.getImageBlobUrl(id))).then(
            (urls) => {
                if (cancelled) {
                    urls.forEach((u) => URL.revokeObjectURL(u));
                    return;
                }
                urls.forEach((u) => blobUrls.push(u));

                imgs.forEach((img, i) => {
                    img.setAttribute("src", urls[i]);
                    img.setAttribute("style", "max-width:100%;display:block;margin:8px 0");
                });

                setHtml(doc.body.innerHTML);
            },
        );

        return () => {
            cancelled = true;
            blobUrls.forEach((u) => URL.revokeObjectURL(u));
        };
    }, [body, communicationId]);

    if (html === null && body) {
        return (
            <Flex justify="center" style={{ padding: 16 }}>
                <Spin size="small" />
            </Flex>
        );
    }

    return (
        <EditorProvider>
            <Editor
                className="comm-body-renderer"
                containerProps={{ className: "comm-body-renderer-wrap" }}
                value={html ?? ""}
                disabled
            />
        </EditorProvider>
    );
}
